import React, { useEffect, useState, useRef, useMemo } from 'react';
import AppConst from "../apis/appconst";
import axios from "axios";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

/* ============================================================
 *  MASTER DROPDOWN DATA
 * ============================================================ */

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  // Union Territories
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir",
  "Ladakh", "Lakshadweep", "Puducherry"
];

const RELIGIONS = [
  "Hindu", "Muslim", "Christian", "Sikh", "Buddhist", "Jain", "Parsi", "Jewish", "Other"
];

const OCCUPATIONS = [
  "Service (Private)", "Service (Government)", "Business", "Farmer", "Labour / Daily Wage",
  "Driver", "Student", "Housewife", "Self Employed", "Retired", "Unemployed", "Other"
];

const NATURE_OF_INJURY = [
  "Blunt Injury", "Sharp / Incised Wound", "Lacerated Wound", "Fracture", "Burn",
  "Firearm Injury", "Poisoning", "Snake / Animal Bite", "Drowning",
  "Hanging / Strangulation", "Electrocution", "Fall from Height", "Head Injury",
  "Multiple Injuries", "Other"
];

const MLC_TYPES = [
  "Road Traffic Accident", "Assault / Fight", "Poisoning", "Burn Injury",
  "Suicide Attempt", "Industrial Accident", "Sexual Offence", "Domestic Violence",
  "Found Unconscious / Unknown", "Brought Dead", "Other"
];

const COUNTRIES = ["India", "Nepal", "Bangladesh", "Bhutan", "Sri Lanka", "Myanmar", "Other"];
const PATIENT_TYPES = ["OPD", "IPD", "Emergency", "Casualty", "Brought Dead", "Referred"];
const MARITAL_STATUS = ["Single", "Married", "Divorced", "Widowed", "Separated"];
const PATIENT_TITLES = ["Mr.", "Mrs.", "Miss", "Ms.", "Master", "Baby", "Dr.", "Smt.", "Shri"];
const SDW_TITLES = ["S/o", "D/o", "W/o", "C/o", "H/o", "M/o", "F/o"];
const GENDERS = ["Male", "Female", "Other"];

// turn ["A","B"] into [{value:"A",label:"A"}, ...]
const toOptions = (arr) => arr.map(x => ({ value: x, label: x }));

/* ============================================================
 *  SEARCHABLE SELECT
 *  Dependency-free: type to filter, arrow keys to move,
 *  Enter to pick, Esc to close, click outside to close.
 * ============================================================ */

const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "-- Select --",
  disabled = false,
  allowClear = true,
  emptyText = "No match found",
  autoFocusOnOpen = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find(o => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // startsWith matches float to the top, then plain contains
    const starts = [];
    const contains = [];
    options.forEach(o => {
      const label = String(o.label || '').toLowerCase();
      if (label.startsWith(q)) starts.push(o);
      else if (label.includes(q)) contains.push(o);
    });
    return [...starts, ...contains];
  }, [options, query]);

  // close on outside click
  useEffect(() => {
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  useEffect(() => { setHighlight(0); }, [query, open]);

  // keep the highlighted row visible while arrowing
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    if (autoFocusOnOpen) setTimeout(() => inputRef.current?.focus(), 0);
  };

  const choose = (opt) => {
    onChange(opt ? opt.value : "");
    setOpen(false);
    setQuery("");
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openMenu(); return; }
      setHighlight(h => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open) {
        e.preventDefault();
        if (filtered[highlight]) choose(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery("");
    } else if (e.key === 'Tab') {
      setOpen(false);
      setQuery("");
    } else if (e.key === 'Backspace' && !query && allowClear && selected) {
      onChange("");
    }
  };

  const displayValue = open ? query : (selected ? selected.label : "");

  return (
    <div className="ss-wrap" ref={wrapRef}>
      <div className={`ss-control form-control ${disabled ? 'ss-disabled' : ''} ${open ? 'ss-open' : ''}`}
        onClick={openMenu}>
        <input
          ref={inputRef}
          type="text"
          className="ss-input"
          value={displayValue}
          placeholder={selected ? selected.label : placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={openMenu}
          onKeyDown={onKeyDown}
        />

        {allowClear && selected && !disabled && (
          <span className="ss-clear" title="Clear" onClick={clear}>&times;</span>
        )}
        <span className="ss-caret">
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </span>
      </div>

      {open && (
        <ul className="ss-menu" ref={listRef}>
          {filtered.length === 0 && (
            <li className="ss-empty">{emptyText}</li>
          )}
          {filtered.map((o, idx) => {
            const isSel = selected && String(selected.value) === String(o.value);
            return (
              <li
                key={`${o.value}-${idx}`}
                data-idx={idx}
                className={`ss-option ${idx === highlight ? 'ss-active' : ''} ${isSel ? 'ss-selected' : ''}`}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(o)}
              >
                {o.label}
                {isSel && <i className="ri-check-line ss-tick"></i>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* styles for the searchable select (injected once from the page) */
const ssStyles = `
.ss-wrap { position: relative; width: 100%; }
.ss-control {
  display: flex; align-items: center; gap: 6px;
  padding: 0 8px 0 12px; cursor: text; min-height: 38px;
}
.ss-control.ss-open { border-color: #405189; box-shadow: 0 0 0 .15rem rgba(64,81,137,.15); }
.ss-control.ss-disabled { background: #e9ecef; cursor: not-allowed; }
.ss-input {
  flex: 1 1 auto; border: 0; outline: 0; background: transparent;
  font-size: inherit; color: inherit; min-width: 0; padding: 6px 0;
}
.ss-input::placeholder { color: #98a2b3; }
.ss-clear {
  cursor: pointer; color: #98a2b3; font-size: 19px; line-height: 1;
  padding: 0 4px; flex: 0 0 auto;
}
.ss-clear:hover { color: #d92d20; }
.ss-caret { color: #667085; flex: 0 0 auto; display: flex; font-size: 17px; }
.ss-menu {
  position: absolute; z-index: 1056; top: calc(100% + 3px); left: 0; right: 0;
  max-height: 240px; overflow-y: auto; margin: 0; padding: 4px;
  list-style: none; background: #fff; border: 1px solid #e4e7ec;
  border-radius: 6px; box-shadow: 0 8px 24px rgba(16,24,40,.12);
}
.ss-option {
  padding: 8px 10px; border-radius: 4px; cursor: pointer; font-size: 14px;
  color: #344054; display: flex; align-items: center; justify-content: space-between;
}
.ss-option.ss-active { background: #f1f5fb; color: #405189; }
.ss-option.ss-selected { font-weight: 600; }
.ss-tick { color: #405189; font-size: 15px; }
.ss-empty { padding: 12px 10px; color: #98a2b3; font-size: 13px; text-align: center; }
`;

/* ============================================================
 *  BLANK FORM
 * ============================================================ */

const getLocalYYYYMMDD = (d = new Date()) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getLocalHHMMSS = (d = new Date()) => {
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mi}:${ss}`;
};

/* ---- display formatters (12-hour, dd-mm-yyyy) ---- */

// "2026-10-29 13:04:00"  ->  "29-10-2026 01:04 PM"
const fmtDateTime12h = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim().replace('T', ' ');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return String(raw);

  const datePart = `${m[3]}-${m[2]}-${m[1]}`;
  if (m[4] === undefined) return datePart;

  let h = Number(m[4]);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;                       // 0 -> 12 AM, 13 -> 1 PM
  return `${datePart} ${String(h).padStart(2, '0')}:${m[5]} ${ampm}`;
};

// "2026-10-29" -> "29-10-2026"
const fmtDate = (raw) => {
  if (!raw) return '';
  const m = String(raw).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(raw);
};

// "13:04:00" -> "01:04 PM"  (for the hint under the time input)
const fmtTime12h = (raw) => {
  if (!raw) return '';
  const m = String(raw).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  let h = Number(m[1]);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${m[2]} ${ampm}`;
};

const blankForm = () => ({
  mlc_id: "",

  // MLC details
  mlc_no: "",
  mlc_date: getLocalYYYYMMDD(),
  mlc_time: getLocalHHMMSS(),
  mlc_type: "",
  mlc_doctor: "",
  mlc_department: "",

  // Patient details
  mlc_uhid: "",
  mlc_patient_title: "Mr.",
  mlc_patient_name: "",
  mlc_patient_gender: "Male",
  mlc_marital_status: "Single",
  mlc_sdw_title: "S/o",
  mlc_attendant_name: "",
  mlc_mobile: "",
  mlc_dob: "",
  mlc_age_year: "",
  mlc_age_month: "",
  mlc_age_day: "",
  mlc_address: "",
  mlc_country: "India",
  mlc_state: "",
  mlc_city: "",
  mlc_religion: "",
  mlc_occupation: "",
  mlc_nature_injury: "",
  mlc_patient_type: "",

  // Legal details
  mlc_brought_by1_name: "",
  mlc_brought_by1_contact: "",
  mlc_brought_by2_name: "",
  mlc_brought_by2_contact: "",
  mlc_fir_no: "",
  mlc_fir_date: getLocalYYYYMMDD(),
  mlc_inves_officer: "",
  mlc_batch_no: "",
  mlc_police_station: "",
  mlc_identification_marks: "",
  mlc_remarks: "",
});

/* ============================================================
 *  COMPONENT
 * ============================================================ */

const MlcRegistration = () => {

  const navigate = useNavigate();

  const [form, setForm] = useState(blankForm());
  const [docs, setDocs] = useState([]);          // doctors
  const [cats, setCats] = useState([]);          // departments / specialities
  const [headerLogo, setHeaderLogo] = useState('');
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [autoNo, setAutoNo] = useState(true);    // true = number came from server

  const didInit = useRef(false);

  /* ---------------- helpers ---------------- */

  const getStoredUser = () => {
    try {
      const s = sessionStorage.getItem("ldata");
      if (s) return JSON.parse(s);
      const other = localStorage.getItem("data") || localStorage.getItem("ldata");
      if (other) return JSON.parse(other);
    } catch (e) { /* ignore */ }
    return null;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // setter used by every SearchableSelect
  const setField = (name) => (value) =>
    setForm(prev => ({ ...prev, [name]: value }));

  const imageUrlToBase64 = async (url) => {
    if (!url) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) { return null; }
  };

  /* ---------------- OPTION LISTS ---------------- */

  const doctorOptions = useMemo(() => (docs || []).map(d => ({
    value: String(d.user_id),
    label: `${d.fname || ''} ${d.lname || ''}`.trim() || String(d.user_id),
  })), [docs]);

  const departmentOptions = useMemo(() => (cats || []).map(c => ({
    value: String(c.cat_id),
    label: c.cat_name || String(c.cat_id),
  })), [cats]);

  /* ---------------- DOCTOR -> DEPARTMENT AUTO SELECT ----------------
   * The doctor row may carry its speciality under any of several column
   * names, and it may hold either the cat_id OR the department NAME.
   * So: collect every plausible value off the row, then resolve it
   * against the loaded department list by id, then by exact name,
   * then by partial name.
   * ------------------------------------------------------------------ */

  // NOTE: getUserListUrl returns the speciality NESTED inside a `doc`
  // object (doctor.doc.doc_specality) — that is what the IPD registration
  // page reads. We check the nested objects first, then the flat columns.
  const DOCTOR_SPECIALITY_FIELDS = [
    'doc_specality', 'doc_speciality', 'doc_department', 'doc_dept', 'doc_cat',
    'user_speciality', 'user_specality', 'user_department', 'user_dept',
    'user_cat', 'user_cat_id', 'user_spec', 'user_speciality_id',
    'user_speciality_name', 'user_department_name', 'user_dept_name',
    'speciality', 'specality', 'specility', 'specialization',
    'department', 'dept', 'spec_name', 'cat_id', 'cat_name',
  ];

  // nested objects the API may wrap the doctor profile in
  const NESTED_KEYS = ['doc', 'user', 'profile', 'details', 'doctor', 'dt'];

  // every value on the doctor row that could possibly be a department
  const collectSpecialityCandidates = (doctor) => {
    if (!doctor) return [];
    const out = [];

    const push = (v) => {
      if (v === undefined || v === null) return;
      if (typeof v === 'object') return;
      const s = String(v).trim();
      if (s === '' || s === '0' || s.toLowerCase() === 'null') return;
      // a field may hold "3,7" or "Ortho, Surgery" - split it
      s.split(',').forEach(part => {
        const p = part.trim();
        if (p) out.push(p);
      });
    };

    // scan one object (flat): named fields first, then anything spec/dept-ish
    const scan = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      DOCTOR_SPECIALITY_FIELDS.forEach(f => push(obj[f]));
      Object.keys(obj).forEach(k => {
        if (/spec|depart|dept/i.test(k) && !DOCTOR_SPECIALITY_FIELDS.includes(k)) {
          push(obj[k]);
        }
      });
    };

    // 1) nested profile objects first - this is where doc_specality lives
    NESTED_KEYS.forEach(k => scan(doctor[k]));

    // 2) the row itself
    scan(doctor);

    // 3) last resort: any other nested object one level deep
    Object.keys(doctor).forEach(k => {
      if (!NESTED_KEYS.includes(k) && doctor[k] && typeof doctor[k] === 'object'
          && !Array.isArray(doctor[k])) {
        scan(doctor[k]);
      }
    });

    return [...new Set(out)];
  };

  // candidate value -> a cat_id that exists in the department dropdown
  const resolveDepartmentId = (doctor, deptOptions) => {
    const candidates = collectSpecialityCandidates(doctor);
    if (!candidates.length || !deptOptions.length) return "";

    // 1) exact cat_id match
    for (const c of candidates) {
      const hit = deptOptions.find(o => String(o.value) === c);
      if (hit) return hit.value;
    }
    // 2) exact name match (case / space insensitive)
    for (const c of candidates) {
      const lc = c.toLowerCase();
      const hit = deptOptions.find(o => String(o.label).trim().toLowerCase() === lc);
      if (hit) return hit.value;
    }
    // 3) partial name match ("Ortho" vs "Orthopaedics")
    for (const c of candidates) {
      const lc = c.toLowerCase();
      if (lc.length < 3) continue;
      const hit = deptOptions.find(o => {
        const label = String(o.label).trim().toLowerCase();
        return label.includes(lc) || lc.includes(label);
      });
      if (hit) return hit.value;
    }
    return "";
  };

  const onDoctorSelect = (doctorId) => {
    const doctor = docs.find(d => String(d.user_id) === String(doctorId));
    const deptId = resolveDepartmentId(doctor, departmentOptions);

    // ---- DIAGNOSTIC: open the browser console to see what the row holds ----
    if (doctorId) {
      console.groupCollapsed('%c[MLC] Doctor -> Department', 'color:#405189;font-weight:700');
      console.log('Selected doctor row     :', doctor);
      console.log('Speciality candidates   :', collectSpecialityCandidates(doctor));
      console.log('Departments loaded      :', departmentOptions);
      console.log('Resolved department id  :', deptId || '(none)');
      console.groupEnd();
    }

    setForm(prev => ({
      ...prev,
      mlc_doctor: doctorId,
      // auto-fill when we resolved one, otherwise keep whatever the user
      // already picked so a manual choice is never wiped
      mlc_department: deptId ? deptId : prev.mlc_department
    }));

    if (doctorId && !deptId) {
      if (!departmentOptions.length) {
        toast.info("Departments are still loading — please pick one in a moment.");
      } else {
        toast.info("This doctor has no department mapped — please select it manually.");
      }
    }
  };

  // If a doctor was chosen before the department list finished loading,
  // resolve it again as soon as the departments arrive.
  useEffect(() => {
    if (!form.mlc_doctor || form.mlc_department) return;
    if (!departmentOptions.length || !docs.length) return;

    const doctor = docs.find(d => String(d.user_id) === String(form.mlc_doctor));
    const deptId = resolveDepartmentId(doctor, departmentOptions);
    if (deptId) {
      setForm(prev => ({ ...prev, mlc_department: deptId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, departmentOptions, form.mlc_doctor]);

  /* ---------------- DOB -> AGE AUTO CALC ---------------- */
  const onDobChange = (e) => {
    const dob = e.target.value;
    if (!dob) {
      setForm(prev => ({ ...prev, mlc_dob: "" }));
      return;
    }
    const d = new Date(dob + "T00:00:00");
    if (isNaN(d)) {
      setForm(prev => ({ ...prev, mlc_dob: dob }));
      return;
    }
    const now = new Date();
    let years = now.getFullYear() - d.getFullYear();
    let months = now.getMonth() - d.getMonth();
    let days = now.getDate() - d.getDate();
    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      days += prevMonth;
    }
    if (months < 0) { years -= 1; months += 12; }
    if (years < 0) { years = 0; months = 0; days = 0; }

    setForm(prev => ({
      ...prev,
      mlc_dob: dob,
      mlc_age_year: String(years),
      mlc_age_month: String(months),
      mlc_age_day: String(days),
    }));
  };

  /* ---------------- TITLE -> GENDER ---------------- */
  const onTitleSelect = (title) => {
    const t = String(title || '').toLowerCase();
    let gender = form.mlc_patient_gender;
    if (t.startsWith('mr.') || t.startsWith('master') || t.startsWith('shri')) gender = 'Male';
    else if (t.startsWith('mrs') || t.startsWith('miss') || t.startsWith('ms.') || t.startsWith('baby') || t.startsWith('smt')) gender = 'Female';
    setForm(prev => ({ ...prev, mlc_patient_title: title, mlc_patient_gender: gender }));
  };

  /* ============================================================
   *  DATA LOADERS
   * ============================================================ */

  const loadDoctors = async () => {
    const fd = new FormData();
    fd.append('type', "doctor");
    fd.append('search_key', "");
    fd.append('page_no', "none");
    fd.append('user_cre_by', "1");
    try {
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.status) setDocs(res.data.records || []);
    } catch (e) {
      console.error("loadDoctors error:", e);
    }
  };

  const loadDepartments = async () => {
    const fd = new FormData();
    fd.append('cat_type', "speciality");
    fd.append('page_no', "none");
    fd.append('par_by', "1");
    try {
      const res = await axios.post(AppConst.BASE_URL + AppConst.getCatListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.status) setCats(res.data.records || []);
    } catch (e) {
      console.error("loadDepartments error:", e);
    }
  };

  const loadNextMlcNo = async () => {
    try {
      const res = await axios.post(AppConst.BASE_URL + AppConst.getMlcNextNoUrl, new FormData());
      if (res.data.status && res.data.mlc_no) {
        setForm(prev => ({ ...prev, mlc_no: res.data.mlc_no }));
        setAutoNo(true);
      }
    } catch (e) {
      console.warn("Could not auto-generate MLC number:", e);
      toast.warn("Auto MLC number unavailable — please type one.");
      setAutoNo(false);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await axios.post(`${AppConst.BASE_URL + AppConst.getconfig}?config_type=lab`);
      if (res.data.status) setHeaderLogo(res.data.header_logo);
    } catch (e) { /* ignore */ }
  };

  /* ============================================================
   *  MOUNT
   * ============================================================ */

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    loadDoctors();
    loadDepartments();
    loadConfig();

    // Edit mode? the list page stores the row in sessionStorage
    let editRow = null;
    try {
      const raw = sessionStorage.getItem("mlc_dt");
      if (raw) editRow = JSON.parse(raw);
    } catch (e) { /* ignore */ }

    if (editRow && editRow.mlc_id) {
      setIsEdit(true);
      setAutoNo(false);
      setForm({
        ...blankForm(),
        ...editRow,
        mlc_id: String(editRow.mlc_id || ""),
        mlc_doctor: String(editRow.mlc_doctor ?? ""),
        mlc_department: String(editRow.mlc_department ?? ""),
        mlc_date: (editRow.mlc_date || "").toString().slice(0, 10) || getLocalYYYYMMDD(),
        mlc_time: (editRow.mlc_time || "").toString().slice(0, 8) || getLocalHHMMSS(),
        mlc_dob: editRow.mlc_dob ? String(editRow.mlc_dob).slice(0, 10) : "",
        mlc_fir_date: editRow.mlc_fir_date ? String(editRow.mlc_fir_date).slice(0, 10) : "",
        mlc_age_year: String(editRow.mlc_age_year ?? ""),
        mlc_age_month: String(editRow.mlc_age_month ?? ""),
        mlc_age_day: String(editRow.mlc_age_day ?? ""),
      });
    } else {
      setIsEdit(false);
      loadNextMlcNo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================================================
   *  VALIDATION
   * ============================================================ */

  const validate = () => {
    if (!String(form.mlc_no).trim()) return "MLC No. is required";
    if (!String(form.mlc_date).trim()) return "MLC date is required";
    if (!String(form.mlc_patient_name).trim()) return "Patient name is required";
    if (!String(form.mlc_doctor).trim()) return "Please select the attending doctor";
    if (!String(form.mlc_type).trim()) return "Please select the Type of MLC";
    if (!String(form.mlc_nature_injury).trim()) return "Please select the Nature of Injury";

    const mob = String(form.mlc_mobile).trim();
    if (mob && !/^[0-9+\-\s]{6,15}$/.test(mob)) return "Mobile number looks invalid";

    return null;
  };

  /* ============================================================
   *  SAVE
   * ============================================================ */

  const handleSave = async (printAfter = true) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }

    const user = getStoredUser();

    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      fd.append(k, v === null || v === undefined ? "" : String(v));
    });

    if (user) {
      fd.append('user_id', user.user_id || '');
      fd.append('user_name', `${user.fname || ''} ${user.lname || ''}`.trim());
      fd.append('user_role', user.role_id || user.role_name || '');
    }

    setSaving(true);
    try {
      const res = await axios.post(AppConst.BASE_URL + AppConst.createMlcUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data && res.data.status) {
        toast.success(res.data.success || "MLC saved successfully");
        const saved = res.data.mlc_dt || { ...form };

        try {
          sessionStorage.removeItem("mlc_dt");
          sessionStorage.setItem("mlc_refresh", "1");
        } catch (e) { /* ignore */ }

        if (printAfter) {
          await printMlcReceipt(saved);
        }

        navigate('/mlc_registration_list');
      } else {
        toast.error(res.data?.success || "Failed to save MLC record");
      }
    } catch (e) {
      console.error("MLC save error:", e);
      if (e?.response) console.error("Server:", e.response.data);
      toast.error("Server error while saving MLC record");
    } finally {
      setSaving(false);
    }
  };

  /* ============================================================
   *  CANCEL -> back to list
   * ============================================================ */

  const handleCancel = () => {
    Swal.fire({
      title: 'Discard this entry?',
      text: "Any unsaved details will be lost.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'Keep editing'
    }).then((result) => {
      if (result.isConfirmed) {
        try { sessionStorage.removeItem("mlc_dt"); } catch (e) { /* ignore */ }
        navigate('/mlc_registration_list');
      }
    });
  };

  /* ============================================================
   *  RECEIPT PDF
   * ============================================================ */

  const printMlcReceipt = async (row) => {
    if (!row) return;

    const doctorName =
      row.mlc_doctor_name ||
      (docs.find(d => String(d.user_id) === String(row.mlc_doctor))?.fname) || '';
    const deptName =
      row.mlc_department_name ||
      (cats.find(c => String(c.cat_id) === String(row.mlc_department))?.cat_name) || '';

    const ageStr = row.mlc_age || [
      row.mlc_age_year > 0 ? `${row.mlc_age_year} Year${row.mlc_age_year > 1 ? 's' : ''}` : '',
      row.mlc_age_month > 0 ? `${row.mlc_age_month} Month${row.mlc_age_month > 1 ? 's' : ''}` : '',
      row.mlc_age_day > 0 ? `${row.mlc_age_day} Day${row.mlc_age_day > 1 ? 's' : ''}` : '',
    ].filter(Boolean).join(' ');

    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let y = 0;

    const headerImageBase64 = await imageUrlToBase64(headerLogo);
    if (headerImageBase64) {
      doc.addImage(headerImageBase64, 'PNG', 0, 0, pageWidth, 80);
      y = 95;
    } else {
      y = 50;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setDrawColor(0);
    doc.rect(margin, y, pageWidth - margin * 2, 25);
    doc.text("MEDICO LEGAL CASE (MLC) REGISTRATION", pageWidth / 2, y + 17, { align: 'center' });
    y += 42;

    doc.setFontSize(10);
    const leftX = margin + 6;
    const rightX = pageWidth / 2 + 20;
    const labelW = 100;

    const printLine = (label, value, x, yPos) => {
      doc.setFont('helvetica', 'normal');
      doc.text(String(label), x, yPos);
      doc.text(":", x + labelW, yPos);
      doc.setFont('helvetica', 'bold');
      const txt = doc.splitTextToSize(String(value || '-'), (pageWidth / 2) - labelW - 40);
      doc.text(txt, x + labelW + 8, yPos);
      doc.setFont('helvetica', 'normal');
      return (txt.length - 1) * 11;
    };

    const sectionHeader = (title, yPos) => {
      doc.setFillColor(241, 245, 251);
      doc.rect(margin, yPos - 11, pageWidth - margin * 2, 17, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(64, 81, 137);
      doc.text(title, margin + 6, yPos);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      return yPos + 20;
    };

    // 12-hour on the receipt, e.g. "29-10-2026 01:04 PM"
    const dtStr = fmtDateTime12h(
      row.mlc_datetime ||
      `${(row.mlc_date || '').toString().slice(0, 10)} ${(row.mlc_time || '').toString().slice(0, 8)}`
    );

    y = sectionHeader("MLC DETAILS", y);
    printLine("MLC No.", row.mlc_no, leftX, y);
    printLine("Date & Time", dtStr, rightX, y);
    y += 15;
    printLine("Type of MLC", row.mlc_type, leftX, y);
    printLine("Department", deptName, rightX, y);
    y += 15;
    printLine("Doctor", doctorName, leftX, y);
    printLine("Type of Patient", row.mlc_patient_type, rightX, y);
    y += 24;

    y = sectionHeader("PATIENT DETAILS", y);
    printLine("UHID", row.mlc_uhid, leftX, y);
    printLine("Patient Name", `${row.mlc_patient_title || ''} ${row.mlc_patient_name || ''}`.trim(), rightX, y);
    y += 15;
    printLine("Age", ageStr, leftX, y);
    printLine("Gender", row.mlc_patient_gender, rightX, y);
    y += 15;
    printLine("Marital Status", row.mlc_marital_status, leftX, y);
    printLine("Mobile", row.mlc_mobile, rightX, y);
    y += 15;
    printLine("S/D/W o", `${row.mlc_sdw_title || ''} ${row.mlc_attendant_name || ''}`.trim(), leftX, y);
    printLine("Religion", row.mlc_religion, rightX, y);
    y += 15;
    printLine("Occupation", row.mlc_occupation, leftX, y);
    printLine("Nature of Injury", row.mlc_nature_injury, rightX, y);
    y += 15;
    const addrExtra = printLine(
      "Address",
      `${row.mlc_address || ''}${row.mlc_city ? ', ' + row.mlc_city : ''}${row.mlc_state ? ', ' + row.mlc_state : ''}`,
      leftX, y
    );
    printLine("Country", row.mlc_country, rightX, y);
    y += 15 + addrExtra + 9;

    y = sectionHeader("LEGAL DETAILS", y);

    autoTable(doc, {
      startY: y,
      head: [['Brought By', 'Name', 'Contact']],
      body: [
        ['I', row.mlc_brought_by1_name || '-', row.mlc_brought_by1_contact || '-'],
        ['II', row.mlc_brought_by2_name || '-', row.mlc_brought_by2_contact || '-'],
      ],
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 5, lineWidth: 0.5, lineColor: [0, 0, 0] },
      headStyles: { fillColor: [241, 245, 251], textColor: 0, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 70, halign: 'center' } },
      margin: { left: margin, right: margin }
    });

    y = doc.lastAutoTable.finalY + 18;

    printLine("Case DD/FIR No.", row.mlc_fir_no, leftX, y);
    printLine("FIR Date", fmtDate(row.mlc_fir_date), rightX, y);
    y += 15;
    printLine("Inves. Officer", row.mlc_inves_officer, leftX, y);
    printLine("Batch No.", row.mlc_batch_no, rightX, y);
    y += 15;
    const psExtra = printLine("Police Station", row.mlc_police_station, leftX, y);
    y += 15 + psExtra;
    const idExtra = printLine("Identification Marks", row.mlc_identification_marks, leftX, y);
    y += 15 + idExtra + 10;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    const decl = doc.splitTextToSize(
      "This is a Medico Legal Case. Intimation has been given to the concerned police station as per statutory requirement. " +
      "This document is issued for official record and must be preserved as per hospital retention policy.",
      pageWidth - margin * 2
    );
    doc.text(decl, margin, y);
    y += decl.length * 10 + 40;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.line(margin, y, margin + 150, y);
    doc.text("Duty Doctor", margin, y + 14);
    doc.line(pageWidth - margin - 180, y, pageWidth - margin, y);
    doc.text("Authorized Signature", pageWidth - margin, y + 14, { align: 'right' });

    doc.setFontSize(7);
    doc.setTextColor(120);
    doc.text(
      `Printed on: ${new Date().toLocaleString('en-IN')}`,
      margin, doc.internal.pageSize.getHeight() - 25
    );

    window.open(doc.output('bloburl'), '_blank');
  };

  /* ============================================================
   *  SMALL PRESENTATION HELPERS
   * ============================================================ */

  const sectionTitle = (text) => (
    <div className="row align-items-center" style={{ margin: '18px 0 10px' }}>
      <div className="col-auto">
        <div style={{
          border: '1px solid #d0d5dd',
          borderRadius: '4px',
          padding: '6px 28px',
          fontWeight: 600,
          color: '#344054',
          background: '#fff'
        }}>
          {text}
        </div>
      </div>
      <div className="col">
        <hr style={{ margin: 0, borderTop: '1px solid #e4e7ec' }} />
      </div>
    </div>
  );

  const Label = ({ children, required }) => (
    <label className="col-form-label" style={{ fontSize: 14, color: '#344054' }}>
      {children} {required && <span style={{ color: '#d92d20' }}>*</span>}
    </label>
  );

  /* ============================================================
   *  RENDER
   * ============================================================ */

  return (
    <div className="page-content">
      <style>{ssStyles}</style>

      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">

              {/* ===== HEADER ===== */}
              <div className="card-header border-0">
                <div className="row align-items-center gy-3">
                  <div className="col-sm">
                    <h2 style={{ color: "#405189", margin: 0 }}>
                      {isEdit ? 'Edit MLC Registration' : 'MLC Registration'}
                    </h2>
                  </div>
                  <div className="col-sm-auto">
                    <button className="btn btn-soft-secondary" onClick={handleCancel}>
                      <i className="ri-arrow-left-line align-bottom me-1"></i> Back to List
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <form onSubmit={(e) => e.preventDefault()}>

                  {/* ==================================================
                      MLC DETAILS
                  ================================================== */}
                  {sectionTitle('MLC Details')}

                  <div className="row g-3 align-items-center">
                    <div className="col-md-1"><Label required>Date &amp; Time</Label></div>
                    <div className="col-md-2">
                      <input type="date" className="form-control" name="mlc_date"
                        value={form.mlc_date} onChange={onChange} />
                    </div>
                    <div className="col-md-2">
                      <input type="time" step="1" className="form-control" name="mlc_time"
                        value={form.mlc_time} onChange={onChange} />
                      <small className="text-muted" style={{ fontSize: 11 }}>
                        {fmtTime12h(form.mlc_time)}
                      </small>
                    </div>

                    <div className="col-md-1"><Label required>MLC No.</Label></div>
                    <div className="col-md-2">
                      <div className="input-group">
                        <input type="text" className="form-control" name="mlc_no"
                          placeholder="MLC No."
                          value={form.mlc_no}
                          onChange={(e) => { setAutoNo(false); onChange(e); }} />
                        {!isEdit && (
                          <button className="btn btn-outline-secondary" type="button"
                            title="Re-generate number" onClick={loadNextMlcNo}>
                            <i className="ri-refresh-line"></i>
                          </button>
                        )}
                      </div>
                      <small className="text-muted" style={{ fontSize: 11 }}>
                        {autoNo ? 'Auto generated — you can overwrite it' : 'Manually entered'}
                      </small>
                    </div>

                    <div className="col-md-1"><Label required>Type of MLC</Label></div>
                    <div className="col-md-3">
                      <SearchableSelect
                        options={toOptions(MLC_TYPES)}
                        value={form.mlc_type}
                        onChange={setField('mlc_type')}
                        placeholder="-- Type of MLC --"
                      />
                    </div>
                  </div>

                  <div className="row g-3 align-items-center mt-1">
                    <div className="col-md-1"><Label required>Doctor</Label></div>
                    <div className="col-md-4">
                      <SearchableSelect
                        options={doctorOptions}
                        value={form.mlc_doctor}
                        onChange={onDoctorSelect}
                        placeholder="Type to search doctor..."
                        emptyText="No doctor found"
                      />
                    </div>

                    <div className="col-md-1"><Label>Department</Label></div>
                    <div className="col-md-4">
                      <SearchableSelect
                        options={departmentOptions}
                        value={form.mlc_department}
                        onChange={setField('mlc_department')}
                        placeholder="-- Select Department --"
                        emptyText="No department found"
                      />
                      <small className="text-muted" style={{ fontSize: 11 }}>
                        Auto-filled from the selected doctor
                      </small>
                    </div>
                  </div>

                  {/* ==================================================
                      PATIENT DETAILS
                  ================================================== */}
                  {sectionTitle('Patient Details')}

                  <div className="row g-3 align-items-center">
                    <div className="col-md-1"><Label>UHID</Label></div>
                    <div className="col-md-3">
                      <input type="text" className="form-control" name="mlc_uhid"
                        placeholder="UHID (optional)" value={form.mlc_uhid} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label required>Patient Name</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(PATIENT_TITLES)}
                        value={form.mlc_patient_title}
                        onChange={onTitleSelect}
                        placeholder="Title"
                        allowClear={false}
                      />
                    </div>
                    <div className="col-md-5">
                      <input type="text" className="form-control" name="mlc_patient_name"
                        placeholder="Patient Name" value={form.mlc_patient_name} onChange={onChange} />
                    </div>
                  </div>

                  <div className="row g-3 align-items-center mt-1">
                    <div className="col-md-1"><Label>S/D/W o</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(SDW_TITLES)}
                        value={form.mlc_sdw_title}
                        onChange={setField('mlc_sdw_title')}
                        placeholder="S/D/W o"
                        allowClear={false}
                      />
                    </div>
                    <div className="col-md-3">
                      <input type="text" className="form-control" name="mlc_attendant_name"
                        placeholder="Attendant / Guardian Name"
                        value={form.mlc_attendant_name} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Mobile</Label></div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" name="mlc_mobile"
                        placeholder="Mobile Number" maxLength={15}
                        value={form.mlc_mobile} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Gender</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(GENDERS)}
                        value={form.mlc_patient_gender}
                        onChange={setField('mlc_patient_gender')}
                        placeholder="Gender"
                        allowClear={false}
                      />
                    </div>
                  </div>

                  <div className="row g-3 align-items-center mt-1">
                    <div className="col-md-1"><Label>Marital</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(MARITAL_STATUS)}
                        value={form.mlc_marital_status}
                        onChange={setField('mlc_marital_status')}
                        placeholder="Marital Status"
                      />
                    </div>

                    <div className="col-md-1"><Label>DOB / Age</Label></div>
                    <div className="col-md-2">
                      <input type="date" className="form-control" name="mlc_dob"
                        value={form.mlc_dob} onChange={onDobChange} />
                    </div>
                    <div className="col-md-2">
                      <input type="number" min="0" className="form-control" name="mlc_age_year"
                        placeholder="Age (Y)" value={form.mlc_age_year} onChange={onChange} />
                    </div>
                    <div className="col-md-2">
                      <input type="number" min="0" max="11" className="form-control" name="mlc_age_month"
                        placeholder="Age (M)" value={form.mlc_age_month} onChange={onChange} />
                    </div>
                    <div className="col-md-2">
                      <input type="number" min="0" max="31" className="form-control" name="mlc_age_day"
                        placeholder="Age (D)" value={form.mlc_age_day} onChange={onChange} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-1"><Label>Address</Label></div>
                    <div className="col-md-5">
                      <textarea className="form-control" name="mlc_address" rows={2}
                        placeholder="Patient Address" value={form.mlc_address} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Resident</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(COUNTRIES)}
                        value={form.mlc_country}
                        onChange={setField('mlc_country')}
                        placeholder="Country"
                        allowClear={false}
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="row g-2">
                        <div className="col-12">
                          <SearchableSelect
                            options={toOptions(INDIAN_STATES)}
                            value={form.mlc_state}
                            onChange={setField('mlc_state')}
                            placeholder="-- Select State --"
                            emptyText="No state found"
                          />
                        </div>
                        <div className="col-12">
                          <input type="text" className="form-control" name="mlc_city"
                            placeholder="City / District" value={form.mlc_city} onChange={onChange} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 align-items-center mt-1">
                    <div className="col-md-1"><Label>Religion</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(RELIGIONS)}
                        value={form.mlc_religion}
                        onChange={setField('mlc_religion')}
                        placeholder="-- Religion --"
                      />
                    </div>

                    <div className="col-md-1"><Label>Occupation</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(OCCUPATIONS)}
                        value={form.mlc_occupation}
                        onChange={setField('mlc_occupation')}
                        placeholder="-- Occupation --"
                      />
                    </div>

                    <div className="col-md-1"><Label required>Nature of Injury</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(NATURE_OF_INJURY)}
                        value={form.mlc_nature_injury}
                        onChange={setField('mlc_nature_injury')}
                        placeholder="-- Nature --"
                      />
                    </div>

                    <div className="col-md-1"><Label>Type of Patient</Label></div>
                    <div className="col-md-2">
                      <SearchableSelect
                        options={toOptions(PATIENT_TYPES)}
                        value={form.mlc_patient_type}
                        onChange={setField('mlc_patient_type')}
                        placeholder="-- Type --"
                      />
                    </div>
                  </div>

                  {/* ==================================================
                      LEGAL DETAILS
                  ================================================== */}
                  {sectionTitle('Legal Details')}

                  <div className="row g-3 align-items-center">
                    <div className="col-md-1"><Label>Brought By (I)</Label></div>
                    <div className="col-md-3">
                      <input type="text" className="form-control" name="mlc_brought_by1_name"
                        placeholder="Name" value={form.mlc_brought_by1_name} onChange={onChange} />
                    </div>
                    <div className="col-md-1"><Label>Contact</Label></div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" name="mlc_brought_by1_contact"
                        placeholder="Contact" value={form.mlc_brought_by1_contact} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Brought By (II)</Label></div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" name="mlc_brought_by2_name"
                        placeholder="Name" value={form.mlc_brought_by2_name} onChange={onChange} />
                    </div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" name="mlc_brought_by2_contact"
                        placeholder="Contact" value={form.mlc_brought_by2_contact} onChange={onChange} />
                    </div>
                  </div>

                  <div className="row g-3 align-items-center mt-1">
                    <div className="col-md-1"><Label>Case DD/FIR No.</Label></div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" name="mlc_fir_no"
                        placeholder="FIR No." value={form.mlc_fir_no} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Date</Label></div>
                    <div className="col-md-2">
                      <input type="date" className="form-control" name="mlc_fir_date"
                        value={form.mlc_fir_date} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Inves. Officer</Label></div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" name="mlc_inves_officer"
                        placeholder="Officer Name" value={form.mlc_inves_officer} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Batch No.</Label></div>
                    <div className="col-md-2">
                      <input type="text" className="form-control" name="mlc_batch_no"
                        placeholder="Batch No." value={form.mlc_batch_no} onChange={onChange} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-1"><Label>Police Station</Label></div>
                    <div className="col-md-5">
                      <textarea className="form-control" name="mlc_police_station" rows={3}
                        placeholder="Police Station Address"
                        value={form.mlc_police_station} onChange={onChange} />
                    </div>

                    <div className="col-md-1"><Label>Identification Marks</Label></div>
                    <div className="col-md-5">
                      <textarea className="form-control" name="mlc_identification_marks" rows={3}
                        placeholder="Identification Marks"
                        value={form.mlc_identification_marks} onChange={onChange} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-1"><Label>Remarks</Label></div>
                    <div className="col-md-11">
                      <textarea className="form-control" name="mlc_remarks" rows={2}
                        placeholder="Any additional notes"
                        value={form.mlc_remarks} onChange={onChange} />
                    </div>
                  </div>

                  {/* ==================================================
                      ACTIONS
                  ================================================== */}
                  <div className="row mt-4">
                    <div className="col-12 d-flex justify-content-center gap-2">
                      <button type="button" className="btn btn-light px-4"
                        onClick={handleCancel} disabled={saving}>
                        Cancel
                      </button>

                      <button type="button" className="btn btn-outline-primary px-4"
                        onClick={() => handleSave(false)} disabled={saving}>
                        {saving ? 'Saving...' : (isEdit ? 'Update' : 'Save')}
                      </button>

                      <button type="button" className="btn btn-primary px-5"
                        onClick={() => handleSave(true)} disabled={saving}>
                        <i className="ri-printer-line me-1"></i>
                        {saving ? 'Saving...' : (isEdit ? 'Update & Print' : 'Add & Print')}
                      </button>
                    </div>
                  </div>

                </form>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MlcRegistration;